import { useEffect, useRef, type ReactNode } from 'react'
import { useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { useHoverCapable } from '@/components/motion'

/** Cursor reach of the magnify, in px, and the largest scale. */
const REACH = 220
const MAX_SCALE = 1.35

/**
 * Full-bleed strip of product-UI tiles drifting right to left (40s loop),
 * with a dock magnify: tiles within 220px of the cursor scale up to 1.35×
 * on a cosine falloff, eased per frame. The CSS marquee pauses on hover, so
 * the magnify settles on still tiles and the loop stops once settled.
 *
 * Fine pointers only; static under reduced motion (the global rule stops the
 * marquee and the magnify is never attached). Paused off-screen. Decorative.
 */
export function DockMarquee({
  tiles,
  duration = 40,
  className,
}: {
  tiles: ReactNode[]
  duration?: number
  className?: string
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const fine = useHoverCapable()
  const reduced = useReducedMotion()
  const magnify = fine && !reduced

  // Pause the drift while the strip is off-screen.
  useEffect(() => {
    const root = rootRef.current
    const track = trackRef.current
    if (!root || !track) return
    const io = new IntersectionObserver(([entry]) => {
      track.style.animationPlayState = entry?.isIntersecting ? '' : 'paused'
    })
    io.observe(root)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!magnify || !root) return
    const items = Array.from(root.querySelectorAll<HTMLElement>('[data-dock-tile]'))
    const scales = items.map(() => 1)
    let pointerX = -1e4
    let inside = false
    let raf = 0
    let running = false

    const frame = () => {
      let settling = false
      items.forEach((el, i) => {
        const rect = el.getBoundingClientRect()
        const distance = Math.abs(pointerX - (rect.left + rect.width / 2))
        const target =
          inside && distance < REACH
            ? 1 + (MAX_SCALE - 1) * (0.5 + 0.5 * Math.cos((Math.PI * distance) / REACH))
            : 1
        let s = scales[i]! + (target - scales[i]!) * 0.2
        if (Math.abs(target - s) < 0.002) s = target
        else settling = true
        scales[i] = s
        const inner = el.firstElementChild as HTMLElement | null
        if (inner) inner.style.transform = s === 1 ? '' : `scale(${s.toFixed(4)})`
        el.style.zIndex = s > 1.005 ? String(Math.round(s * 100)) : ''
      })
      if (settling) raf = requestAnimationFrame(frame)
      else running = false
    }
    const kick = () => {
      if (running) return
      running = true
      raf = requestAnimationFrame(frame)
    }
    const onMove = (e: PointerEvent) => {
      pointerX = e.clientX
      inside = true
      kick()
    }
    const onLeave = () => {
      inside = false
      kick()
    }
    root.addEventListener('pointermove', onMove)
    root.addEventListener('pointerleave', onLeave)
    return () => {
      cancelAnimationFrame(raf)
      root.removeEventListener('pointermove', onMove)
      root.removeEventListener('pointerleave', onLeave)
      items.forEach((el) => {
        el.style.zIndex = ''
        const inner = el.firstElementChild as HTMLElement | null
        if (inner) inner.style.transform = ''
      })
    }
  }, [magnify, tiles.length])

  return (
    <div ref={rootRef} aria-hidden className={cn('relative overflow-hidden py-12', className)}>
      <div ref={trackRef} className="animate-marquee flex w-max" style={{ animationDuration: `${duration}s` }}>
        {[0, 1].map((copy) => (
          <div key={copy} className="flex shrink-0 gap-3 pr-3 md:gap-4 md:pr-4">
            {tiles.map((tile, i) => (
              <div key={i} data-dock-tile className="relative w-32 shrink-0 md:w-40">
                <div className="aspect-[10/13.5] w-full shadow-[0_14px_30px_-18px_rgba(10,10,10,0.35)]">
                  {tile}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default DockMarquee
